import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Award, ArrowRight, SkipForward } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

// Define type for the poem details map entry (consistent with DeathMatchContest)
interface PoemDetails {
  title: string;
  poet: string;
}

interface RoundSummaryProps {
  bracketPositions: Array<{ 
    poet: string;
    poemIndex: number;
    poemId: string;
    poet_id?: string;
  }>;
  pairings: Array<[number, number]>;
  winners: Array<number | null>;
  championPosition: number | null;
  onAdvanceToNextRound?: () => void;
  isFinalRound?: boolean;
  currentRoundType?: string;
  nextRoundType?: string;
  poemDetailsMap?: Map<string, PoemDetails>;
  onClose: () => void;
  onReturnToBracket: () => void;
  totalPairings: number;
  currentPairingIndex: number;
  limitToPairingIndex?: number;
}

// Determine the round type (e.g., "First Round", "Semi-finals", etc.)
const getRoundType = (roundIndex: number, totalRounds: number, currentPairings: Array<[number, number]>): string => {
  // In the context of our specific tournament structure:
  // Check if we're showing only semifinal pairings (which start at index 4)
  if (currentPairings.length === 2) {
    return "Semifinal Round";
  }
  
  // Check if we're showing only the final pairing
  if (currentPairings.length === 1) {
    return "Final Round";
  }
  
  // For initial rounds display
  if (currentPairings.length === 4) {
    return "Starting Round";
  }
  
  // Fallback based on round number
  if (roundIndex >= 6) {
    return "Final Round";
  } else if (roundIndex >= 4) {
    return "Semifinal Round";
  } else {
    return "Starting Round";
  }
};

const RoundSummary = ({ 
  bracketPositions,
  pairings,
  winners,
  championPosition,
  onAdvanceToNextRound,
  isFinalRound = false,
  currentRoundType = "Current Round",
  nextRoundType = "Next Round",
  onClose,                 // <--- Added
  onReturnToBracket,       // <--- Added
  totalPairings,           // <--- Added
  currentPairingIndex,
  poemDetailsMap = new Map(),
  limitToPairingIndex
}: RoundSummaryProps) => {
  // Determine if we need to show the advance button (only if not final round and handler provided)
  // MODIFIED: Show in modal context too, unless it's the absolute final round.
  const showAdvanceButton = 
    onAdvanceToNextRound !== undefined && 
    winners[totalPairings - 1] === null; // Show as long as the final match isn't decided

  // Generate appropriate heading based on round type
  const getCompletionHeading = () => {
    // Check modal context first
    if (limitToPairingIndex !== undefined) {
      if (limitToPairingIndex === 3) return "You've finished the starting rounds!";
      if (limitToPairingIndex === 5) return "You've finished the semifinal rounds!";
    }
    // Fallback for full summary or potentially final round modal
    if (winners[totalPairings - 1] !== null) return "Deathmatch Final Results";
    // Default (shouldn't ideally be reached in modal context if logic is right)
    return "Round Complete!"; 
  };

  // Generate appropriate button text based on next round type
  const getAdvanceButtonText = () => {
    // Check modal context first
    if (limitToPairingIndex !== undefined) {
      if (limitToPairingIndex === 3) return "Continue to the semifinal rounds";
      if (limitToPairingIndex === 5) return "Continue to the final round";
    }
    // Fallback/Default (e.g., for a final round modal if implemented differently)
    return "Advance to Next Round"; 
  };
  
  // --- Helper function to get title and poet from map or fallbacks ---
  const getDetails = (position: number | null): PoemDetails => {
    if (position === null) {
      return { title: "Unknown Poem", poet: "Unknown Poet" };
    }
    const poemId = bracketPositions[position]?.poemId;
    if (!poemId) {
      return { title: "Unknown Poem", poet: bracketPositions[position]?.poet || "Unknown Poet" };
    }
    const details = poemDetailsMap.get(poemId);
    return {
      title: details?.title || "Unknown Poem",
      poet: details?.poet || bracketPositions[position]?.poet || "Unknown Poet"
    };
  };
  // -----------------------------------------------------------------

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-poetry mb-2">
          {getCompletionHeading()}
        </h2>
        
        {showAdvanceButton && (
          <Button 
            className="mt-6 bg-amber-500 hover:bg-amber-600 gap-2"
            onClick={onAdvanceToNextRound}
            size="lg"
          >
            {getAdvanceButtonText()}
            <SkipForward className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      {/* Champion section - only show in final round */}
      {isFinalRound && winners[6] !== null && (
        <Card className="border-amber-200 bg-amber-50/50 shadow-md mb-8">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-center gap-2 text-amber-800">
              <Trophy className="h-6 w-6 text-amber-500" />
              Champion
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="font-bold text-xl mb-1">
              {getDetails(winners[6]).title}
            </div>
            <div className="text-gray-700 italic text-sm mb-2">
              Inspired by the style of {getDetails(winners[6]).poet}
            </div>
            <Badge variant="outline" className="mt-2 px-2 border-amber-300 text-amber-700">
              <Award className="h-3.5 w-3.5 mr-1" />
              Finalist
            </Badge>
          </CardContent>
        </Card>
      )}
      
      {/* All rounds */}
      <div className="space-y-6">
        {pairings.slice().reverse().map((pairing, reversedIndex) => {
          // Calculate the original index since we reversed the array
          const index = pairings.length - 1 - reversedIndex;
          
          if (limitToPairingIndex !== undefined && index > limitToPairingIndex) {
            return null;
          }
          
          // *** START Fix: Determine actual participants for this round ***
          let actualPosition1: number | null = null;
          let actualPosition2: number | null = null;
          
          if (index >= 0 && index <= 3) { // Quarterfinals
            actualPosition1 = pairing[0];
            actualPosition2 = pairing[1];
          } else if (index === 4) { // Semifinal 1
            actualPosition1 = winners[0]; // Winner of pairing index 0
            actualPosition2 = winners[1]; // Winner of pairing index 1
          } else if (index === 5) { // Semifinal 2
            actualPosition1 = winners[2]; // Winner of pairing index 2
            actualPosition2 = winners[3]; // Winner of pairing index 3
          } else if (index === 6) { // Final
            actualPosition1 = winners[4]; // Winner of pairing index 4
            actualPosition2 = winners[5]; // Winner of pairing index 5
          }
          // *** END Fix ***

          // Calculate the correct winner index (logic remains the same as before, seems correct for identifying winner of *this* round)
          let winnerIndex = index; // Default to current index for quarterfinals
          if (pairings.length === 2) { // Semifinal modal context
            winnerIndex = index + 4;
          } else if (pairings.length === 1) { // Final modal context
            winnerIndex = 6;
          }
          
          const winnerPosition = winners[winnerIndex];
          
          // Restore roundNumber calculation needed for display/separators
          const roundNumber = pairings.length === 4 ? (index + 1) : 
                              pairings.length === 2 ? (index + 5) : 7;
          
          // *** START Fix: Simplify winner highlighting logic ***
          // Directly use the winner determined for this specific round index
          const displayWinnerPosition = winners[index];
          // *** END Fix ***
          
          const roundType = getRoundType(index, pairings.length, pairings);
          
          if (index >= pairings.length) return null;
          
          const isRound4 = roundNumber === 4 || (pairings.length === 7 && index === 3);
          const isRound6 = roundNumber === 6 || (pairings.length === 7 && index === 5);
          
          // *** START Fix: Use actual positions for getting details ***
          const details1 = getDetails(actualPosition1);
          const details2 = getDetails(actualPosition2);
          // *** END Fix ***
          
          return (
            <React.Fragment key={index}>
              {(isRound4 || isRound6) && (
                <div className="border-t border-gray-300 my-4"></div>
              )}
              <Card className={cn(
                "border-gray-200 hover:border-gray-300 transition-all",
                index === pairings.length - 1 && isFinalRound && "border-blue-200 bg-blue-50/50",
                winners[winnerIndex] !== null && "border-green-100"
              )}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-medium text-gray-700 flex items-center">
                    <span className="mr-1">{roundType}</span>
                    <Badge variant="outline" className="ml-2 text-xs">
                      {/* Display the round number */}
                      {pairings.length === 2 ? 
                        `Round ${roundNumber}` : 
                        pairings.length === 1 ? 
                        "Round 7" : 
                        `Round ${index + 1}`
                      }
                    </Badge>
                    {winners[winnerIndex] !== null && (
                      <Badge variant="outline" className="ml-auto text-xs bg-green-50 text-green-700 border-green-200">
                        Completed
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between gap-4">
                    {/* *** START Fix: Update highlighting condition *** */}
                    <div className={cn(
                      "p-3 rounded-md transition-all flex-1",
                      displayWinnerPosition !== null && displayWinnerPosition === actualPosition1 ? "bg-green-50 border border-green-100" : "opacity-80"
                    )}>
                    {/* *** END Fix *** */}
                      <div className="font-medium">
                        {details1.title}
                      </div>
                      <div className="text-sm text-gray-600 italic">
                        Inspired by the style of {details1.poet}
                      </div>
                      {/* *** START Fix: Update highlighting condition *** */}
                      {displayWinnerPosition !== null && displayWinnerPosition === actualPosition1 && (
                      // *** END Fix *** 
                        <Badge variant="outline" className="mt-1 border-green-300 text-green-700 text-xs">
                          Moves On
                        </Badge>
                      )}
                    </div>
                    
                    {/* *** START Fix: Update highlighting condition *** */}
                    <div className={cn(
                      "p-3 rounded-md transition-all flex-1",
                      displayWinnerPosition !== null && displayWinnerPosition === actualPosition2 ? "bg-green-50 border border-green-100" : "opacity-80"
                    )}>
                    {/* *** END Fix *** */}
                      <div className="font-medium">
                        {details2.title}
                      </div>
                      <div className="text-sm text-gray-600 italic">
                        Inspired by the style of {details2.poet}
                      </div>
                       {/* *** START Fix: Update highlighting condition *** */}
                      {displayWinnerPosition !== null && displayWinnerPosition === actualPosition2 && (
                       // *** END Fix *** 
                        <Badge variant="outline" className="mt-1 border-green-300 text-green-700 text-xs">
                          Moves On
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default RoundSummary; 